import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload, AlertTriangle } from "lucide-react";
import { fmtEur, fmtData, MEZZI } from "@/lib/banche";

const Riga = ({ r, i, onToggle }) => (
  <div className={`rounded-md border p-2 text-xs flex items-start gap-2 ${r.duplicato ? "border-amber-300 bg-amber-50" : ""}`} data-testid={`up-riga-${i}`}>
    <div className="min-w-0 flex-1">
      <div className="flex justify-between gap-2">
        <span className="font-medium">{fmtData(r.data)} · {MEZZI[r.mezzo]?.label}</span>
        <span className={`font-bold tabular-nums ${r.importo > 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtEur(r.importo)}</span>
      </div>
      <div className="text-muted-foreground truncate">{r.descrizione}</div>
    </div>
    {r.duplicato && (
      <div className="flex flex-col gap-1 shrink-0">
        <Button size="sm" variant={r.aggiungi ? "outline" : "default"} className="h-7 text-[11px]" onClick={() => onToggle(i, false)} data-testid={`up-salta-${i}`}>Salta</Button>
        <Button size="sm" variant={r.aggiungi ? "default" : "outline"} className="h-7 text-[11px]" onClick={() => onToggle(i, true)} data-testid={`up-aggiungi-${i}`}>Aggiungi</Button>
      </div>
    )}
  </div>
);

export const BancaUpload = ({ onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [an, setAn] = useState(null);
  const [esito, setEsito] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      let { data } = await api.post("/banche/analizza", fd, { timeout: 60000 });
      for (let i = 0; data.in_corso && i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        data = (await api.get(`/banche/analizza/${data.import_id}`)).data;
      }
      if (data.in_corso) throw new Error("timeout");
      setAn({ ...data, righe: data.righe.map((r) => ({ ...r, aggiungi: !r.duplicato })) });
      setEsito(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Lettura del file non riuscita"); }
    finally { setBusy(false); }
  };
  const toggle = (i, v) => setAn((a) => ({ ...a, righe: a.righe.map((r, j) => (j === i ? { ...r, aggiungi: v } : r)) }));
  const tutti = (v) => setAn((a) => ({ ...a, righe: a.righe.map((r) => (r.duplicato ? { ...r, aggiungi: v } : r)) }));
  const conferma = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/banche/conferma", { import_id: an.import_id, banca: an.banca, righe: an.righe });
      setEsito(data);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const dup = an?.righe.filter((r) => r.duplicato).length || 0;

  return (
    <>
      <input ref={ref} type="file" accept=".pdf,.xls,.xlsx,.csv" className="hidden" onChange={onFile} data-testid="banca-file-input" />
      <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="banca-upload-btn">
        {busy && !an ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        {busy && !an ? "Lettura in corso (fino a 1-2 min)…" : "Carica estratto conto"}
      </Button>
      <Dialog open={!!an} onOpenChange={(o) => !o && setAn(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="banca-upload-dialog">
          <DialogHeader><DialogTitle>{an?.filename}</DialogTitle></DialogHeader>
          {an && !esito && (
            <div className="space-y-3 min-w-0">
              <div>
                <Label>Banca</Label>
                <Input value={an.banca} onChange={(e) => setAn({ ...an, banca: e.target.value })} placeholder="Nome della banca" data-testid="up-banca" />
              </div>
              <div className="text-sm">{an.righe.length} movimenti letti{dup > 0 && <span className="text-amber-700"> · {dup} già presenti</span>}</div>
              {an.controllo && (
                <div className={`text-xs rounded-md border p-2 ${an.controllo.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="up-controllo">
                  {an.controllo.ok ? "Totali verificati con l'estratto conto: " : "Attenzione, i totali non coincidono con l'estratto conto: controlla i movimenti. "}
                  addebiti {fmtEur(an.controllo.addebiti)}{!an.controllo.ok && ` (documento ${fmtEur(an.controllo.addebiti_doc)})`} · accrediti {fmtEur(an.controllo.accrediti)}{!an.controllo.ok && ` (documento ${fmtEur(an.controllo.accrediti_doc)})`}
                </div>
              )}
              {dup > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="up-doppioni">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />Movimenti con stessa data, importo e descrizione:
                  <Button size="sm" variant="outline" className="h-7" onClick={() => tutti(false)} data-testid="up-salta-tutti">Salta tutti</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => tutti(true)} data-testid="up-aggiungi-tutti">Aggiungi tutti</Button>
                </div>
              )}
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">{an.righe.map((r, i) => <Riga key={i} r={r} i={i} onToggle={toggle} />)}</div>
              <Button className="w-full h-11" onClick={conferma} disabled={busy} data-testid="up-conferma">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Importa {an.righe.filter((r) => r.aggiungi).length} movimenti
              </Button>
            </div>
          )}
          {esito && (
            <div className="space-y-3" data-testid="up-esito">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                {[["Importati", esito.importati, "up-n-importati"], ["Saltati", esito.saltati, "up-n-saltati"], ["Collegati da soli", esito.collegati, "up-n-collegati"], ["Da collegare", esito.da_collegare, "up-n-dacollegare"]].map(([l, v, t]) => (
                  <div key={t} className="rounded-md border p-2"><div className="text-xl font-bold" data-testid={t}>{v}</div><div className="text-[11px] text-muted-foreground">{l}</div></div>
                ))}
              </div>
              <Button className="w-full" onClick={() => setAn(null)} data-testid="up-chiudi">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
